import { ThemeEvents, CartUpdatedEvent } from '@theme/events';

/**
 * Morfil (mf7) P5 — accorde les cadeaux du pack (protège-lames, protège-doigt) au nombre de couteaux du panier.
 * Lot de 3 couteaux = 1 protège-lame au nom de chacun + 1 protège-doigt, en lignes cadeau (_gift_block_id = mf7-pack).
 * Données : <script data-mf7-pack-panier> (snippet mf7-pack-panier). Une seule exécution à la fois ; l'événement que
 * ce script émet lui-même relance une vérification qui ne trouve plus rien à changer.
 * Garde-fous (relecture du 05/10) :
 * - une modification refusée par Shopify n'est pas retentée à l'identique dans la session (pas de boucle de rechargement
 *   sur la page panier) ;
 * - thème publié : un cadeau du pack facturé signifie qu'une remise automatique manque ; les cadeaux sont alors retirés
 *   pour la session, jamais facturés d'office ;
 * - le bouton de paiement est désactivé pendant les modifications.
 * window.mf7PackAccorder({ recharger: false }) : même travail, sans recharger la page panier (encart mf7-pack-relance).
 */
const BLOC = 'mf7-pack';
const CLE_ECHEC = 'mf7-pack:echec';
const CLE_COUPE = 'mf7-pack:cadeaux-coupes';
let enCours = null;
let encore = false;

function donnees() {
  const el = document.querySelector('[data-mf7-pack-panier]');
  if (!el) return null;
  try {
    return JSON.parse(el.textContent);
  } catch (error) {
    console.error('mf7-pack-panier : données illisibles', error);
    return null;
  }
}

function estCadeauPack(item) {
  return item.properties?._gift_block_id === BLOC;
}

function session(cle, valeur) {
  try {
    if (valeur === undefined) return sessionStorage.getItem(cle);
    if (valeur === null) sessionStorage.removeItem(cle);
    else sessionStorage.setItem(cle, valeur);
  } catch {
    return null;
  }
  return null;
}

function pagePanier() {
  return document.body.classList.contains('template-cart') || location.pathname.replace(/\/$/, '').endsWith('/cart');
}

function lirePanier() {
  return fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
}

async function envoyer(url, corps) {
  const reponse = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(corps),
  });
  const data = await reponse.json().catch(() => ({}));
  return reponse.ok && !(data.status >= 400) ? data : null;
}

/** Un passage : lit le panier, calcule les cadeaux voulus, applique les écarts. Renvoie true si le panier a changé. */
async function accorderUneFois({ recharger = true } = {}) {
  const data = donnees();
  if (!data) return false;

  const cart = await lirePanier();

  if (data.publie && cart.items.some((l) => estCadeauPack(l) && l.final_line_price > 0)) {
    if (session(CLE_COUPE) !== '1') {
      console.warn('mf7-pack-panier : cadeaux du pack facturés (remise automatique absente ?), retirés pour cette session');
    }
    session(CLE_COUPE, '1');
  }
  const coupe = data.publie && session(CLE_COUPE) === '1';

  // Couteaux du panier, dans l'ordre des lignes (les cadeaux ne comptent pas)
  const noms = [];
  for (const item of cart.items) {
    if (item.properties?._is_gift === 'true') continue;
    const nom = data.couteaux?.[String(item.product_id)];
    if (!nom) continue;
    for (let i = 0; i < item.quantity; i += 1) noms.push(nom);
  }
  const lots = Math.floor(noms.length / 3);

  // Les couteaux qui ont déjà leur protège-lame passent en premier : un 4e couteau ne prend pas l'étui d'un couteau du pack
  const nomParEtui = {};
  for (const [nom, id] of Object.entries(data.etuis || {})) nomParEtui[String(id)] = nom;
  const dejaEtui = {};
  for (const ligne of cart.items.filter(estCadeauPack)) {
    const nom = nomParEtui[String(ligne.variant_id)];
    if (nom) dejaEtui[nom] = (dejaEtui[nom] || 0) + ligne.quantity;
  }
  const prioritaires = [];
  const autres = [];
  for (const nom of noms) {
    if (dejaEtui[nom] > 0) {
      prioritaires.push(nom);
      dejaEtui[nom] -= 1;
    } else {
      autres.push(nom);
    }
  }
  const retenus = [...prioritaires, ...autres].slice(0, lots * 3);

  const voulu = {};
  if (!coupe) {
    for (const nom of retenus) {
      const etui = data.etuis?.[nom];
      if (etui) voulu[etui] = (voulu[etui] || 0) + 1;
    }
    if (data.doigt && lots > 0) voulu[data.doigt] = (voulu[data.doigt] || 0) + lots;
  }

  const reste = { ...voulu };
  const changements = [];
  for (const ligne of cart.items.filter(estCadeauPack)) {
    const garder = Math.min(ligne.quantity, reste[ligne.variant_id] || 0);
    reste[ligne.variant_id] = (reste[ligne.variant_id] || 0) - garder;
    if (garder !== ligne.quantity) changements.push({ key: ligne.key, quantity: garder });
  }
  const ajouts = Object.entries(reste)
    .filter(([, q]) => q > 0)
    .map(([id, q]) => ({
      id: Number(id),
      quantity: q,
      properties: { _is_gift: 'true', _gift_badge: data.badge || 'Offert', _gift_block_id: BLOC },
    }));

  if (changements.length === 0 && ajouts.length === 0) return false;

  // Même écart déjà refusé par Shopify dans cette session : on ne recommence pas (sinon boucle sur la page panier)
  const signature = JSON.stringify({ voulu, lignes: cart.items.map((l) => `${l.key}:${l.quantity}`) });
  if (session(CLE_ECHEC) === signature) return false;

  const tiroir = document.querySelector('cart-drawer');
  const sections = tiroir?.dataset.sectionId || '';
  const avecSections = (corps) => (sections ? { ...corps, sections } : corps);

  const boutons = Array.from(document.querySelectorAll('[data-ref="checkout-button"]:not([disabled])'));
  boutons.forEach((b) => {
    b.disabled = true;
    b.setAttribute('aria-busy', 'true');
  });

  let dernier = null;
  let echec = false;
  try {
    for (const c of changements) {
      const reponse = await envoyer(Theme.routes.cart_change_url, avecSections({ id: c.key, quantity: c.quantity }));
      if (reponse) dernier = reponse;
      else echec = true;
    }
    if (ajouts.length) {
      const reponse = await envoyer(Theme.routes.cart_add_url, avecSections({ items: ajouts }));
      if (reponse) dernier = reponse;
      else echec = true;
    }
  } finally {
    boutons.forEach((b) => {
      b.disabled = false;
      b.removeAttribute('aria-busy');
    });
  }

  if (echec) {
    session(CLE_ECHEC, signature);
    console.warn('mf7-pack-panier : Shopify a refusé une partie des cadeaux du pack');
  } else {
    session(CLE_ECHEC, null);
  }
  if (!dernier) return false;

  if (pagePanier()) {
    if (recharger) location.reload();
    return true;
  }

  const nouveau = await lirePanier();
  // Thème publié : les cadeaux tout juste ajoutés doivent être à 0 € ; sinon le passage suivant les retire
  if (data.publie && !coupe && nouveau.items.some((l) => estCadeauPack(l) && l.final_line_price > 0)) encore = true;
  document.dispatchEvent(new CartUpdatedEvent({}, { itemCount: nouveau.item_count, source: BLOC, sections: dernier.sections }));
  return true;
}

/** Accorde les cadeaux ; un appel pendant un passage en cours relance un passage à la fin. Promesse : true si changé. */
function accorder(options = {}) {
  if (enCours) {
    encore = true;
    return enCours;
  }
  enCours = (async () => {
    let change = false;
    do {
      encore = false;
      try {
        change = (await accorderUneFois(options)) || change;
      } catch (error) {
        console.error('mf7-pack-panier : accord des cadeaux impossible', error);
      }
    } while (encore);
    return change;
  })().finally(() => {
    enCours = null;
  });
  return enCours;
}

if (!window.__mf7PackPanier) {
  window.__mf7PackPanier = true;
  window.mf7PackAccorder = accorder;
  document.addEventListener(ThemeEvents.cartUpdated, (event) => {
    if (event.detail?.data?.source === BLOC) return;
    accorder();
  });
  accorder();
}
